import { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

const TeamContext = createContext();

export function useTeam() {
  return useContext(TeamContext);
}

export function TeamProvider({ children }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(() => {
    const savedMember = localStorage.getItem('selectedTeamMember');
    return savedMember ? JSON.parse(savedMember) : null;
  });
  const { user: currentUser } = useAuth();

  // Add this new effect to persist selectedMember to localStorage
  useEffect(() => {
    if (selectedMember) {
      localStorage.setItem('selectedTeamMember', JSON.stringify(selectedMember));
    } else {
      localStorage.removeItem('selectedTeamMember');
    }
  }, [selectedMember]);

  // Fetch team members and handle initial selection
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'teams'),
        where('managerId', '==', currentUser.uid)
      ),
      (snapshot) => {
        const members = snapshot.docs.map(doc => ({
          id: doc.id,
          ref: doc,
          ...doc.data()
        }));
        setTeamMembers(members);

        // Select first member if no member is selected and members exist
        if (!selectedMember && members.length > 0) {
          setSelectedMember(members[0]);
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser]); // Remove selectedMember from dependencies to prevent circular updates

  const addTeamMember = async (teamMember) => {
    try {
      const teamMemberRef = await addDoc(collection(db, 'teams'), teamMember);
      const newTeamMember = {
        ...teamMember,
        id: teamMemberRef.id,
        ref: teamMemberRef
      };
      
      setSelectedMember(newTeamMember);
      return newTeamMember;
    } catch (error) {
      console.error('Error adding team member:', error);
      throw error;
    }
  };

  const updateTeamMember = async (memberId, memberData) => {
    if (!currentUser) throw new Error('No authenticated user');

    try {
      const memberRef = doc(db, 'teams', memberId);
      await updateDoc(memberRef, {
        ...memberData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
  };

  const deleteTeamMember = async (member) => {
    try {
      // Delete the team member document
      await deleteDoc(doc(db, 'teams', member.id));

      // Delete related meetings
      const meetingsRef = collection(db, 'meetings');
      const meetingsQuery = query(
        meetingsRef, 
        where('participantId', '==', doc(db, 'teams', member.id))
      );
      const meetingsSnapshot = await getDocs(meetingsQuery);
      
      const deletePromises = meetingsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Update local state
      setTeamMembers(prevMembers => prevMembers.filter(m => m.id !== member.id));
      if (selectedMember?.id === member.id) {
        setSelectedMember(null);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting team member:', error);
      throw error;
    }
  };

  const value = {
    teamMembers,
    selectedMember,
    setSelectedMember,
    addTeamMember,
    updateTeamMember,
    deleteTeamMember
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
} 